import { apiService, baseService } from '@clarix/api-client';
import type { InviteRecord, WorkspaceMember } from '@clarix/shared-types';

/**
 * Cross-app reads/writes of the ONE workspace membership list (owned/managed in
 * apps/workspace, exposed here so other apps — e.g. the Support Users module —
 * can list "who has access to this workspace and app X" from the same source
 * instead of growing their own user system). `app_access` on each member is the
 * authoritative per-app grant record (`{ code, role, status }`).
 */
const adminBase = (slug: string) => `/workspaces/${slug}/admin`;

export interface WorkspaceMemberListParams {
  page?: number;
  per_page?: number;
  search?: string;
  role?: 'OWNER' | 'ADMIN' | 'MEMBER';
  /** DEACTIVE is a UI bucket spanning PAUSED + SUSPENDED. */
  status?: 'ACTIVE' | 'PAUSED' | 'SUSPENDED' | 'DEACTIVE';
}

export interface WorkspaceMemberListPage {
  items: WorkspaceMember[];
  total: number;
}

/** The gateway's standard list envelope (data + top-level pagination). */
interface ListEnvelope<T> {
  success: boolean;
  data: T;
  pagination?: { total?: number };
}

/** Hard ceiling for the exhaustive fetch — a runaway-pagination backstop. */
const MAX_DIRECTORY_PAGES = 20;
const DIRECTORY_PAGE_SIZE = 100;

export const membersApi = {
  /** One page of workspace members (server-side search/role/status filters). */
  list: async (
    slug: string,
    params: WorkspaceMemberListParams = {},
  ): Promise<WorkspaceMemberListPage> => {
    const res = await baseService.get<ListEnvelope<WorkspaceMember[]>>(
      `${adminBase(slug)}/members`,
      {
        params: {
          page: params.page ?? 1,
          per_page: params.per_page ?? DIRECTORY_PAGE_SIZE,
          ...(params.search ? { search: params.search } : {}),
          ...(params.role ? { role: params.role } : {}),
          ...(params.status ? { status: params.status } : {}),
        },
      },
    );
    const items = res.data.data ?? [];
    return { items, total: res.data.pagination?.total ?? items.length };
  },

  /**
   * Every member of the workspace, walking the pages. Consumers that filter
   * client-side (e.g. "members whose app_access includes SUPPORT") need the
   * whole list — a single server page would silently drop matches.
   */
  listAll: async (slug: string): Promise<WorkspaceMember[]> => {
    const all: WorkspaceMember[] = [];
    for (let page = 1; page <= MAX_DIRECTORY_PAGES; page += 1) {
      const { items, total } = await membersApi.list(slug, {
        page,
        per_page: DIRECTORY_PAGE_SIZE,
      });
      all.push(...items);
      if (items.length < DIRECTORY_PAGE_SIZE || all.length >= total) break;
    }
    return all;
  },

  /** Pending/expired workspace invites (each carries its per-app grants in apps_json). */
  invites: (slug: string) => apiService.get<InviteRecord[]>(`/workspaces/${slug}/invites`),

  /** PUT /admin/users/:userId/apps/:code — idempotent per-member app grant. */
  grantAppAccess: (
    slug: string,
    input: { userId: number; code: string; app_base_role?: string; permission_template_id?: string },
  ) =>
    apiService.put<unknown>(`${adminBase(slug)}/users/${input.userId}/apps/${input.code}`, {
      app_base_role: input.app_base_role ?? `${input.code}.MEMBER`,
      ...(input.permission_template_id
        ? { permission_template_id: input.permission_template_id }
        : {}),
    }),

  /** DELETE /admin/users/:userId/apps/:code — revoke the app grant. */
  revokeAppAccess: (slug: string, input: { userId: number; code: string }) =>
    baseService.delete<void>(`${adminBase(slug)}/users/${input.userId}/apps/${input.code}`),

  /** POST /invites/:id/resend — re-send a pending invitation email. */
  resendInvite: (slug: string, inviteId: string) =>
    baseService.post<void>(`/workspaces/${slug}/invites/${inviteId}/resend`),

  /** DELETE /invites/:id — revoke a pending invitation. */
  revokeInvite: (slug: string, inviteId: string) =>
    baseService.delete<void>(`/workspaces/${slug}/invites/${inviteId}`),
};
